import React from 'react';
import { CheckCircle, Clock, Wrench, Euro } from 'lucide-react';

const SansMaconnerie = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            Installation de Niveleur de Quai <span className="text-orange-500">Sans Maçonnerie</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl">
            Gagnez du temps et de l'argent : installez votre équipement de quai en 2 jours au lieu de 2 semaines, sans travaux de maçonnerie lourds.
          </p>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          Pourquoi Choisir l'Installation Sans Maçonnerie ?
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Installation Rapide</h3>
            <p className="text-slate-600">
              <strong className="text-orange-600">2 jours</strong> au lieu de 2 semaines. Votre quai est opérationnel immédiatement.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <Euro className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Économique</h3>
            <p className="text-slate-600">
              Pas de coûts de maçonnerie, pas de permis de construire. <strong>Économisez jusqu'à 40%</strong> sur l'installation.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <Wrench className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Aucune Perturbation</h3>
            <p className="text-slate-600">
              Votre activité continue pendant l'installation. Pas de gros travaux, pas de poussière.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Totalement Conforme</h3>
            <p className="text-slate-600">
              Respect des normes <strong>R408</strong> et des réglementations de sécurité au travail.
            </p>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Comment ça Fonctionne ?
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Fixation par Chevilles Chimiques</h3>
              <p className="text-slate-600">
                Au lieu de creuser le sol, nous utilisons des fixations haute résistance directement sur la dalle existante.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Structure Autoportante</h3>
              <p className="text-slate-600">
                Nos niveleurs sont conçus pour être autoportants, sans nécessiter de fosse maçonnée.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Installation Express</h3>
              <p className="text-slate-600">
                Pose en 1 à 2 jours maximum. Mise en service immédiate après tests de conformité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparaison */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Comparaison Installation Traditionnelle vs Sans Maçonnerie
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Critère</th>
                  <th className="px-6 py-4 text-center">Avec Maçonnerie</th>
                  <th className="px-6 py-4 text-center bg-orange-600">Sans Maçonnerie (Orion Industrie)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-6 py-4 font-semibold">Durée d'installation</td>
                  <td className="px-6 py-4 text-center text-slate-600">10-15 jours</td>
                  <td className="px-6 py-4 text-center font-bold text-orange-600">1-2 jours</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 font-semibold">Permis de construire</td>
                  <td className="px-6 py-4 text-center text-slate-600">Souvent nécessaire</td>
                  <td className="px-6 py-4 text-center font-bold text-orange-600">Non requis</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold">Perturbation activité</td>
                  <td className="px-6 py-4 text-center text-slate-600">Importante (2 semaines)</td>
                  <td className="px-6 py-4 text-center font-bold text-orange-600">Minimale (2 jours)</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 font-semibold">Travaux de génie civil</td>
                  <td className="px-6 py-4 text-center text-slate-600">Excavation, coulage béton</td>
                  <td className="px-6 py-4 text-center font-bold text-orange-600">Aucun</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold">Conformité Carsat</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Conforme</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Conforme</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 font-semibold">Robustesse</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Très robuste</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Très robuste</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Idéal Pour
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Quais de chargement existants',
              'Rénovation sans arrêt d\'activité',
              'Bâtiments en location (pas de travaux lourds)',
              'Budgets optimisés',
              'Délais serrés',
              'Zones difficiles d\'accès'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow">
                <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0" />
                <span className="text-slate-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Prêt à Installer Votre Niveleur Sans Maçonnerie ?
          </h2>
          <p className="text-xl mb-8 text-orange-100">
            Devis gratuit sous 24h. Intervention rapide partout en France.
          </p>
          <a
            href="/#contact"
            className="inline-block bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 transition-colors"
          >
            Demander un Devis Gratuit
          </a>
        </div>
      </section>
    </div>
  );
};

export default SansMaconnerie;
